<template>
  <div>
    <el-dialog
      width="90%"
      :visible="!!editingEvent"
      :before-close="handleClose"
      :close-on-click-modal="false"
      :modal-append-to-body="false"
      :modal="false"
    >
      <div slot="title">
        <div class="el-dialog__title">
          {{
            $t(
              `Integrations_Settings_WebhookMessageEvents_EditPlayloadDialog`,
              { editingPayloadName: editingPayloadName }
            )
          }}
        </div>
      </div>
      <el-alert
        v-if="payloadIsEmpty"
        style="margin-bottom: 10px"
        :title="$t('Integrations_Settings_Webhook_Empty_Payload')"
        :closable="false"
        type="error"
      />
      <el-form-item>
        <json-editor
          ref="editor"
          :value="payloadModel"
          :template="schemaPlaceholder"
          @value-is-empty="payloadIsEmpty = $event"
          @validation-result="payloadIsValid = $event"
          @value-edited="payloadIsEdited = $event"
        />
      </el-form-item>
      <el-button type="primary" plain @click="resetPayload">
        {{ $t('Integrations_Settings_Webhook_Reset_Payload') }}
      </el-button>
      <el-button type="primary" :disabled="isSaveDisabled" @click="savePayload">
        {{ $t('Integrations_Settings_Webhook_Save_Payload') }}
      </el-button>
    </el-dialog>
    <el-table
      :data="eventsList"
      :stripe="true"
      :model="model"
      :show-header="false"
    >
      <el-table-column width="70" align="left">
        <template slot-scope="scope">
          <el-switch v-model="eventsModel[scope.row.code]" />
        </template>
      </el-table-column>
      <el-table-column>
        <template slot-scope="scope">
          <div class="name">
            {{ scope.row.name }}
          </div>
          <div class="description" v-if="scope.row.description">
            {{ scope.row.description }}
          </div>
        </template>
      </el-table-column>
      <el-table-column align="center" width="120">
        <template slot-scope="scope">
          <div
            class="EventsList--Statuses"
            v-if="isPayloadEdited(scope.row.code)"
          >
            <el-badge is-dot class="EventsList--Status"></el-badge>
            <span>
              {{ $t('Integrations_Settings_WebhookMessageEvents_Mod_Label') }}
            </span>
          </div>
        </template>
      </el-table-column>
      <el-table-column align="center" width="50">
        <template slot-scope="scope">
          <icon
            class="edit-payload"
            glyph="pencil"
            size="16"
            @click="editPayload(scope.row)"
          />
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import JsonEditor from '@/common/components/JsonEditor'
import { payloadTemplates } from '@/modules/integrations/enum/webhook/PayloadTemplates'
import Icon from '@/common/components/Icon'

export default {
  name: 'EventsToggleList',
  components: { Icon, JsonEditor },

  props: {
    model: {
      type: Object,
      default: {}
    },

    eventsList: {
      type: Array,
      default: function() {
        return []
      }
    }
  },
  data() {
    return {
      editingEvent: null,
      payloadIsEmpty: false,
      payloadIsValid: true,
      payloadIsEdited: false
    }
  },
  beforeMount() {
    if (
      !this.model.params.message_event_schemas ||
      (Array.isArray(this.model.params.message_event_schemas) &&
        this.model.params.message_event_schemas.length === 0)
    ) {
      this.$set(this.model.params, 'message_event_schemas', {})
    }
  },
  computed: {
    eventsModel() {
      return this.model.params.message_events
    },
    eventsPayloadModel() {
      return this.model.params.message_event_schemas
    },
    schemaPlaceholder() {
      if (!this.editingEvent) return null

      return this.getPayloadTemplate(this.editingEvent)
    },
    editingPayloadName() {
      let msgObj = this.eventsList.filter(e => {
        return e.code === this.editingEvent
      })
      return msgObj.length > 0 ? msgObj[0].name : ''
    },
    payloadModel: {
      get() {
        return this.eventsPayloadModel[`${this.editingEvent}_payload`]
      },
      set(val) {
        this.$set(this.eventsPayloadModel, `${this.editingEvent}_payload`, val)
      }
    },
    isSaveDisabled() {
      if (!this.editingEvent) return false

      return (
        !this.payloadIsValid || this.payloadIsEmpty || !this.payloadIsEdited
      )
    }
  },
  methods: {
    editPayload(event) {
      this.editingEvent = event.code
      if (this.$refs.editor) {
        this.$nextTick(() => this.$refs.editor.refreshEditor())
      }
    },
    resetPayload() {
      const currentEvent = this.editingEvent
      this.editingEvent = null
      this.$delete(
        this.model.params.message_event_schemas,
        `${currentEvent}_payload`
      )
      if (this.$refs.editor) {
        this.$refs.editor.resetToDefault()
      }
      this.clearEditor()
    },
    savePayload() {
      if (!this.payloadIsEmpty && this.$refs.editor) {
        if (this.$refs.editor.validate() === null) {
          const payloadValue = this.$refs.editor.getPayload()

          if (payloadValue) {
            if (!this.isPayloadDefault(this.editingEvent, payloadValue)) {
              this.$set(
                this.eventsPayloadModel,
                `${this.editingEvent}_payload`,
                payloadValue
              )
              this.handleClose()
            } else {
              this.resetPayload()
            }
          } else {
            this.resetPayload()
          }
        }
      }
    },
    handleClose() {
      this.editingEvent = null
      this.clearEditor()
    },
    clearEditor() {
      if (this.$refs.editor) {
        this.$refs.editor.clear()
      }
    },
    isPayloadDefault(event, payload) {
      return this.getPayloadTemplate(event) === payload
    },
    isPayloadEdited(event) {
      if (!this.eventsPayloadModel.hasOwnProperty(`${event}_payload`)) {
        return false
      }

      return !this.isPayloadDefault(
        event,
        this.eventsPayloadModel[`${event}_payload`]
      )
    },
    getPayloadTemplate(event) {
      return payloadTemplates.hasOwnProperty(event)
        ? JSON.stringify(payloadTemplates[event], null, 4)
        : undefined
    }
  }
}
</script>

<style scoped>
.edit-payload {
  cursor: pointer;
  vertical-align: middle;
  fill: #a7acba;
}

.el-table:before {
  height: 0;
}

.EventsList--Statuses {
  display: flex;
  align-items: center;
  gap: 10px;
}

.EventsList--Status {
  display: flex;
  margin-left: 16px;
}

>>> .EventsList--Status .el-badge__content {
  background-color: #1d87c8;
}
</style>



// WEBPACK FOOTER //
// src/modules/integrations/types/webhook/EventsToggleList.vue